import { Inject, Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { Loader } from '@googlemaps/js-api-loader';
import { Address } from '../_models/address';

declare let google: any;

interface LatLngLiteral {
  lat: number;
  lng: number;
}

@Injectable()
export class GMapsService {
  private geocoder: any;
  private mapsAPILoader: Loader;

  constructor(
    @Inject('googleMapsApiKey') private apiKey: string,
    @Inject('googleMapsLibraries') private libraries: any
  ) {
    this.mapsAPILoader = new Loader({
      apiKey: this.apiKey,
      libraries: this.libraries,
    });
    this.mapsAPILoader.load().then(() => {
      this.geocoder = new google.maps.Geocoder();
    });
  }

  getLatLan(address: string): Observable<any> {
    return new Observable((observer) => {
      this.geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          observer.next(results[0].geometry.location);
        } else {
          observer.next({});
          observer.error('Geocoder failed due to: ' + status);
        }
        observer.complete();
      });
    });
  }

  getAddressFromGMaps(
    location: LatLngLiteral,
    addressResult: Address = new Address()
  ): Observable<Address> {
    return new Observable<Address>((observer: Observer<Address>) => {
      this.geocoder.geocode({ location }, (results, status) => {
        if (
          status === google.maps.GeocoderStatus.OK &&
          results &&
          results.length > 0 &&
          results[0]
        ) {
          const geocoderResult: google.maps.places.PlaceResult = results[0];
          if (geocoderResult) {
            let streetNumber = '',
              street1 = '';
            let isBrazil: boolean = null;

            // Funzione per elaborare i componenti dell'indirizzo
            const processAddressComponents = (
              component: google.maps.GeocoderAddressComponent,
              isBrazil: boolean
            ) => {
              if (component.types.includes('country')) {
                addressResult.country = component.long_name;
                addressResult.countryCode = component.short_name;
              }

              if (component.types.includes('route')) {
                street1 = component.long_name !== 'Unnamed Road' ? component.long_name : null;
              }

              if (component.types.includes('street_number')) {
                streetNumber = component.long_name;
              }

              if (isBrazil) {
                // Se il paese è il Brasile, aggiorna città e contea in base ai componenti specifici
                geocoderResult.address_components.forEach((element) => {
                  if (element.types.includes('administrative_area_level_2')) {
                    addressResult.city = element.long_name;
                  }
                  if (element.types.includes('administrative_area_level_1')) {
                    addressResult.county = element.long_name;
                  }
                });
              } else {
                // Se il paese non è il Brasile, usa altre logiche
                if (component.types.includes('locality')) {
                  addressResult.city = component.long_name;
                } else if (component.types.includes('administrative_area_level_2')) {
                  addressResult.county = component.short_name;
                }
              }

              if (component.types.includes('postal_code')) {
                addressResult.zipCode = component.short_name;
              }
            };

            // Ciclo attraverso i componenti dell'indirizzo
            geocoderResult.address_components.forEach(
              (component: google.maps.GeocoderAddressComponent) => {
                if (component.types.includes('country')) {
                  // Determina se il paese è il Brasile
                  isBrazil = component.short_name === 'BR';
                }
                // Chiama la funzione per elaborare i componenti dell'indirizzo
                processAddressComponents(component, isBrazil);
              }
            );

            // Imposta la via e il numero civico
            addressResult.street1 =
              (street1 ? street1 : '') + (streetNumber ? ', ' + streetNumber : '');

            // Imposta latitudine e longitudine
            addressResult.lat = geocoderResult.geometry.location.lat();
            addressResult.lng = geocoderResult.geometry.location.lng();
          }

          // Ritorna il risultato dell'indirizzo
          observer.next(addressResult);
          observer.complete();
        } else {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }
}
